import React from 'react';
import PropTypes from 'prop-types';
import kebabCase from 'lodash/kebabCase';
import stylePropType from 'react-style-proptype';
import {
  Radar,
  RadarChart as Chart,
  Legend,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip,
  ResponsiveContainer,
  Text,
} from 'recharts';
import CustomTooltip from './CustomTooltip';

class RadarChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = { highlightedLabel: null };
  }

  updateLabel = (highlightedLabel, highlightedKey) => {
    const { onHighlightedKeyChange } = this.props;

    this.setState({ highlightedLabel, highlightedKey }, () => {
      onHighlightedKeyChange(highlightedKey);
    });
  }

  handleChartMove = ({ isTooltipActive, activeLabel, activePayload }) => {
    if (!isTooltipActive) return;

    const { highlightedKey } = this.state;
    const activeKey = activePayload[0].payload.key;

    if (activeKey !== highlightedKey) {
      this.updateLabel(activeLabel, activeKey);
    }
  }

  degreesToRadians = angle => (Math.PI / 180) * (angle);

  renderCustomAngleAxisTick = (tickProps) => {
    const { radius, payload: { value, coordinate, index } } = tickProps;
    const { data } = this.props;
    const { link } = data[index];
    const { highlightedLabel } = this.state;

    const angleInDegrees = coordinate;
    const normalisedAngleInDegrees = Math.abs(angleInDegrees) + 90;
    const isNarrowMargin = (normalisedAngleInDegrees > 45 && normalisedAngleInDegrees < 135)
      || (normalisedAngleInDegrees > 225 && normalisedAngleInDegrees < 315);
    const wideMarginMultiplier = 0.08;
    const narrowMarginMultiplier = 0.02;
    const marginMultiplier = isNarrowMargin ? narrowMarginMultiplier : wideMarginMultiplier;
    const distance = radius * marginMultiplier;
    const marginX = distance * Math.cos(this.degreesToRadians(angleInDegrees));
    const marginY = distance * Math.sin(this.degreesToRadians(angleInDegrees));
    const transform = `translate(${marginX},${-(marginY)})`;
    const isHighlightedClassName = `${value === highlightedLabel ? 'tick-active' : ''}`;
    const attributeClassName = kebabCase(data[index].key);
    const className = `attribute-tick tick-${attributeClassName} ${isHighlightedClassName}`;

    const content = (
      <Text
        width={75}
        verticalAnchor="middle"
        {...{ className, transform }}
        {...tickProps}
      >
        {value}
      </Text>
    );

    if (link) {
      return (
        <a href={link} className="attribute-link">
          {content}
        </a>
      );
    }

    return content;
  }

  renderRadarLayers = layers => (
    layers.map(({
      name,
      dataKey,
      fill,
      stroke,
      strokeWidth,
      opacity,
    }, index) => (
      <Radar
        key={dataKey}
        fill={fill}
        stroke={stroke}
        fillOpacity={opacity}
        strokeWidth={strokeWidth}
        animationDuration={750}
        animationBegin={index * 500}
        {...{ name, dataKey }}
      />
    ))
  )

  render() {
    const {
      data, layers, style,
    } = this.props;

    return (
      <div className="radar-chart-container" style={{ ...style, marginBottom: '20px' }}>
        <ResponsiveContainer
          width="100%"
          aspect={0.8}
          minHeight={0}
        >
          <Chart
            className="radar-chart"
            data={data}
            onMouseMove={this.handleChartMove}
            onTouchStart={this.handleChartMove}
            onTouchMove={this.handleChartMove}
            margin={{
              top: 20,
              right: 100,
              bottom: 100,
              left: 20,
            }}
          >
            <Legend
              align="left"
              verticalAlign="top"
              iconType="circle"
              layout="vertical"
              iconSize={20}
              wrapperStyle={{
                // Without this, changing legend size for different comparisons also
                // causes the radar chart to also resize
                width: 0, height: 0, overflow: 'visible',
              }}
            />
            <PolarAngleAxis
              dataKey="attributeName"
              tick={this.renderCustomAngleAxisTick}
            />
            <PolarGrid gridType="circle" stroke="white" strokeWidth={1} />
            {this.renderRadarLayers(layers)}
            <PolarRadiusAxis
              angle={90}
              domain={[0, 100]}
              tick={false}
              tickCount={5}
              orientation="middle"
              strokeOpacity={0}
            />
            <Tooltip
              content={<CustomTooltip />}
              animationDuration={100}
            />
          </Chart>
        </ResponsiveContainer>
      </div>
    );
  }
}

RadarChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.any).isRequired,
  layers: PropTypes.arrayOf(PropTypes.any).isRequired,
  onHighlightedKeyChange: PropTypes.func,
  style: stylePropType,
};
RadarChart.defaultProps = {
  onHighlightedKeyChange: () => { },
  style: {},
};

export default RadarChart;
